import { useEffect, useRef, useState } from 'react'
import Markdown from 'react-markdown'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import SendIcon from '@mui/icons-material/Send'
import { ChatApiError } from '../../apiErrors'
import { Button } from '../../components/Buttons'
import { t } from '../../i18n'
import {
  autofillQuestionnaire,
  getQuestionnaire,
  submitQuestionnaire,
} from './api'
import { questionnaireCopy, questionnaireError } from './copy'
import {
  activeQuestions,
  cleanAnswers,
  parseAutofill,
  submissionAnswers,
  validateAnswers,
} from './model'
import { QuestionField } from './QuestionField'
import { Answer, Answers, Question } from './types'

const trendQuestionName = 'q24_trendanalyse'

export function QuestionnaireV2({
  chatServer,
  getToken,
}: {
  chatServer: string
  getToken: () => Promise<string | null>
}) {
  const [questions, setQuestions] = useState<Question[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setQuestions(null)
    setError(null)
    const load = async () => {
      try {
        const token = await getToken()
        if (!token) throw new ChatApiError(401, 'No access token')
        const schema = await getQuestionnaire(token, chatServer)
        if (!cancelled) setQuestions(schema.questions)
      } catch (error) {
        if (!cancelled) setError(questionnaireError(error, 'load'))
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [chatServer, getToken, attempt])

  const tokenForRequest = async () => {
    const token = await getToken()
    if (!token) throw new ChatApiError(401, 'No access token')
    return token
  }

  if (!questions)
    return (
      <div className="flex-1 p-4">
        {error ? (
          <>
            <p role="alert">{error}</p>
            <Button onClick={() => setAttempt((value) => value + 1)}>
              {questionnaireCopy.retry}
            </Button>
          </>
        ) : (
          <p role="status">{questionnaireCopy.loading}</p>
        )}
      </div>
    )

  return (
    <QuestionnaireForm
      questions={questions}
      autofill={async (text) =>
        autofillQuestionnaire(await tokenForRequest(), chatServer, text)
      }
      submit={async (answers) =>
        submitQuestionnaire(await tokenForRequest(), chatServer, answers)
      }
    />
  )
}

export function QuestionnaireForm({
  questions,
  autofill,
  submit,
}: {
  questions: Question[]
  autofill: (text: string) => Promise<string>
  submit: (answers: Answers) => Promise<void>
}) {
  const [answers, setAnswers] = useState<Answers>(() =>
    cleanAnswers(questions, {}),
  )
  const [text, setText] = useState('')
  const [explanation, setExplanation] = useState('')
  const [hasAutofilled, setHasAutofilled] = useState(false)
  const [busy, setBusy] = useState<'autofill' | 'submit' | null>(null)
  const operation = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [showValidation, setShowValidation] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const errors = showValidation ? validateAnswers(questions, answers) : {}

  const changeAnswer = (name: string, value: Answer) => {
    if (operation.current) return
    setAnswers((current) =>
      cleanAnswers(questions, { ...current, [name]: value }),
    )
    setSubmitted(false)
    setError(null)
  }

  const fill = async () => {
    if (operation.current) return
    operation.current = true
    setBusy('autofill')
    setError(null)
    setSubmitted(false)
    try {
      const result = parseAutofill(questions, await autofill(text))
      setAnswers(result.answers)
      setExplanation(result.explanation)
      setHasAutofilled(true)
      setShowValidation(false)
    } catch (error) {
      setError(questionnaireError(error, 'autofill'))
    } finally {
      operation.current = false
      setBusy(null)
    }
  }

  const send = async () => {
    if (operation.current) return
    setShowValidation(true)
    setError(null)
    setSubmitted(false)
    if (Object.keys(validateAnswers(questions, answers)).length) return
    operation.current = true
    setBusy('submit')
    try {
      await submit(submissionAnswers(questions, answers))
      setSubmitted(true)
    } catch (error) {
      setError(questionnaireError(error, 'submit'))
    } finally {
      operation.current = false
      setBusy(null)
    }
  }

  const clear = () => {
    if (operation.current) return
    setAnswers(cleanAnswers(questions, {}))
    setText('')
    setExplanation('')
    setHasAutofilled(false)
    setShowValidation(false)
    setSubmitted(false)
    setError(null)
  }

  return (
    <section
      aria-label={t('statistics')}
      aria-busy={!!busy}
      className="overflow-y-auto flex-1 bg-gray-100 dark:bg-gray-800 dark:text-white rounded-t-3xl flex flex-col items-center w-full"
    >
      <h1 className="text-3xl">{t('createStatistics')}</h1>
      <div className="flex flex-col gap-4 p-4 md:max-w-4xl w-full">
        <label htmlFor="questionnaire-source">
          {questionnaireCopy.sourceText}
        </label>
        <textarea
          id="questionnaire-source"
          rows={10}
          value={text}
          disabled={!!busy}
          className="p-4 bg-white border dark:bg-gray-800 dark:text-white dark:border-gray-700 rounded-lg w-full"
          onChange={(event) => setText(event.target.value)}
        />
        <div className="flex gap-2">
          <Button
            onClick={fill}
            disabled={!!busy || !text.trim()}
            variant="primary"
          >
            <AutoFixHighIcon />
            {busy === 'autofill' ? t('fillingForm') : t('fillForm')}
          </Button>
          <Button onClick={clear} disabled={!!busy}>
            {t('clearForm')}
          </Button>
        </div>
        {activeQuestions(questions, answers)
          .filter(
            (question) => hasAutofilled || question.name !== trendQuestionName,
          )
          .map((question) => (
            <QuestionField
              key={question.name}
              question={
                question.name === trendQuestionName
                  ? {
                      ...question,
                      text: 'Trendanalyse. Hva kom opp i samtalen.',
                    }
                  : question
              }
              value={answers[question.name]}
              text={
                typeof answers[`${question.name}_text`] === 'string'
                  ? (answers[`${question.name}_text`] as string)
                  : ''
              }
              errors={errors}
              disabled={!!busy}
              onChange={changeAnswer}
            />
          ))}
        {explanation && (
          <div className="p-4 border border-gray-300 dark:border-gray-600 rounded-lg">
            <h2>{t('explanationHeading')}</h2>
            <div className="prose dark:prose-invert">
              <Markdown>{explanation}</Markdown>
            </div>
          </div>
        )}
        {Object.keys(errors).length > 0 && (
          <p role="alert" className="text-red-600 dark:text-red-400">
            {questionnaireCopy.validation}
          </p>
        )}
        {error && (
          <p role="alert" className="text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {questionnaireCopy.stubNotice}
        </p>
        <Button onClick={send} disabled={!!busy} variant="primary">
          <SendIcon />
          {busy === 'submit' ? t('submitting') : t('submitAnswers')}
        </Button>
        {submitted && <p role="status">{questionnaireCopy.submitted}</p>}
      </div>
    </section>
  )
}
